import {SyntheticEvent, useContext, useEffect, useState} from 'react';

import {useTranslation} from 'react-i18next';

import ArchiveIcon from '@mui/icons-material/Archive';
import {Accordion, Box, Grid, Icon, Typography} from '@mui/material';

import JobPostingsAccordionDetails from './AccordionDetails';
import JobPostingsAccordionSummary from './AccordionSummary';
import valid_icon from '../../../../../../assets/job_postings/icons/valid-icon.png';
import {EmpionTooltip} from '../../../../../../components/EmpionTooltip/EmpionTooltip';
import {LanguageContext} from '../../../../../../contexts/LanguageContext/LanguageContext';
import {greenColors} from '../../../../../../layouts/theme/designSystem';
import {LanguageProfile} from '../../../../../../types/shared-types';
import {CompanyLocation, JobPosting} from '../../types/JobPostingsTypes';
interface JobPostingsListViewAccordionProps {
  jobPostings: JobPosting[];
  companyId: string;
  handleUpdateJobPosting(jobPosting: JobPosting): void;
  handleDeleteJobPosting(jobPosting: JobPosting): void;
  handleJobPostingStatusChange(jobPosting: JobPosting | undefined, action: 'archive' | 'activate'): void;
  companyLocations: CompanyLocation[];
  paramsId: string | null;
}

export default function JobPostingsListViewAccordion({
  jobPostings,
  companyId,
  handleUpdateJobPosting,
  handleDeleteJobPosting,
  handleJobPostingStatusChange,
  companyLocations,
  paramsId,
}: JobPostingsListViewAccordionProps) {
  const [expanded, setExpanded] = useState<string | false>();

  useEffect(() => {
    if (paramsId) {
      setExpanded(paramsId);
      const element = document.getElementById(paramsId);
      if (element) {
        element.scrollIntoView({behavior: 'smooth'});
      }
    }
  }, [paramsId]);

  const currentLanguage = useContext(LanguageContext);
  const {t} = useTranslation();

  const onChange = (panel: string) => (_event: SyntheticEvent, newExpanded: boolean) => {
    setExpanded(newExpanded ? panel : false);
  };

  const renderJobPostingLocations = (jobPosting: JobPosting) => {
    let jobPostingLocations: string[] = [];

    if (jobPosting.locations && jobPosting.locations.length > 0) {
      jobPostingLocations = jobPosting.locations.map((location, idx) => {
        const defaultCityName = (location.city as LanguageProfile).en;

        if ((location.city as LanguageProfile)[currentLanguage]) {
          return `${(location.city as LanguageProfile)[currentLanguage]}${
            jobPosting.locations && idx < jobPosting.locations?.length - 1 ? ',' : ''
          } `;
        } else {
          return `${defaultCityName}${jobPosting.locations && idx < jobPosting.locations?.length - 1 ? ',' : ''} `;
        }
      });
    }

    if (jobPosting.remoteHiring) {
      if (jobPostingLocations.length > 0) {
        jobPostingLocations[jobPostingLocations.length - 1] = `${jobPostingLocations[
          jobPostingLocations.length - 1
        ].trim()}, `;
      }

      jobPostingLocations.push('Remote');
    }

    return jobPostingLocations;
  };

  const renderIconStatus = (jobPosting: JobPosting) => {
    if (jobPosting.archivedAt) {
      return (
        <EmpionTooltip title={t('shared.archived')} isEnabled>
          <Icon
            sx={{
              marginLeft: {xs: '0px', sm: '3px'},
            }}
            fontSize='small'
          >
            <ArchiveIcon
              sx={{
                color: greenColors[100],
              }}
            />
          </Icon>
        </EmpionTooltip>
      );
    } else if (jobPosting.publishedAt) {
      return (
        <EmpionTooltip title={t('shared.published')} isEnabled>
          <Box
            sx={{
              width: '16px',
              height: '16px',
              marginLeft: {xs: '4px', sm: '5px'},
            }}
            component={'img'}
            src={valid_icon}
            alt='valid icon'
          />
        </EmpionTooltip>
      );
    } else {
      return null;
    }
  };

  return (
    <Grid container spacing={2} marginTop={4}>
      {jobPostings && jobPostings.length > 0 ? (
        jobPostings?.map((jobPosting) => (
          <Grid item xs={12} key={jobPosting.resourceId} id={jobPosting.resourceId === paramsId ? paramsId : undefined}>
            <Accordion
              elevation={0}
              expanded={expanded === jobPosting.resourceId}
              onChange={onChange(jobPosting.resourceId as string)}
              sx={{
                backgroundColor: 'transparent',
              }}
            >
              <JobPostingsAccordionSummary
                jobPosting={jobPosting}
                renderJobPostingLocations={renderJobPostingLocations}
                expanded={expanded}
                renderIconStatus={renderIconStatus}
              />
              <JobPostingsAccordionDetails
                jobPosting={jobPosting}
                renderJobPostingLocations={renderJobPostingLocations}
                companyId={companyId}
                handleUpdateJobPosting={handleUpdateJobPosting}
                handleDeleteJobPosting={handleDeleteJobPosting}
                handleJobPostingStatusChange={handleJobPostingStatusChange}
                companyLocations={companyLocations}
                renderIconStatus={renderIconStatus}
              />
            </Accordion>
          </Grid>
        ))
      ) : (
        <Grid
          item
          xs={12}
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
          }}
        >
          <Typography
            variant='h2'
            sx={{
              padding: '1rem',
            }}
          >
            {t('shared.noResults')}
          </Typography>
        </Grid>
      )}
    </Grid>
  );
}
